import { Button } from '@/components/ui/button'
import { EmptyState } from '@/components/patterns/empty-state'
import DecimalInput, {
  formatDecimalInputValue,
  parseDecimalInputValue,
} from '@/components/form/DecimalInput'
import AutocompleteSearchInput from '@/components/form/AutocompleteSearchInput'
import { FileDropzone } from '@/components/ui/file-dropzone'
import InlineFieldErrors, { getFieldErrorMessages } from '@/components/form/InlineFieldErrors'
import { IndexDataTable } from '@/components/tables/IndexTable'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { Typography } from '@/components/ui/typography'
import { usePlacesAutocomplete } from '@/hooks/use-places-autocomplete'
import { useIsReadOnly } from '@/lib/casl'
import { Country } from '@/lib/enums/Country'
import { useFormContext } from '@/lib/form-contexts'
import {
  useImportLocationDeliveryPostalCodeFeesMutation,
  usePreviewLocationDeliveryPostalCodeFeeImportMutation,
} from '@/lib/services/locations/locations.query'
import { cn, getErrorMessage } from '@/lib/utils'
import { useStore } from '@tanstack/react-form'
import {
  AlertCircle,
  ExternalLink,
  FileUp,
  MapPin,
  Plus,
  Route,
  Trash2,
  Upload,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { toast } from 'sonner'
import DeliveryCutoffOverrideField from '../DeliveryCutoffOverrideField'

const postalField = 'delivery_postal_code_fees'
const cityField = 'delivery_city_fees'
const distanceRangeField = 'delivery_distance_fee_ranges'
const maxDistanceField = 'delivery_max_distance_miles'
const cutoffOverridesField = 'is_delivery_cutoff_overrides_enabled'
const cityAutocompletePrimaryTypes = ['locality']

const getArrayFieldPath = (field, index, property) => `${field}[${index}].${property}`

const isValidPostalCode = (postalCode) => /^\d{5}$/.test(String(postalCode ?? '').trim())

const updateRow = (rows, index, patch) =>
  rows.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row))

const combineErrorMessages = (...groups) => [...new Set(groups.flat().filter(Boolean))]

const countryDisplayNames = {
  [Country.UnitedStates.Value]: 'USA',
  [Country.Canada.Value]: Country.Canada.Name,
}

const formatCityDisplayName = (city, state, country = Country.UnitedStates.Value) =>
  [city, state, countryDisplayNames[country] ?? country].filter(Boolean).join(', ')

const isCityPlaceSuggestion = (suggestion) => {
  const types = suggestion?.types ?? suggestion?.value?.types ?? []

  return types.length === 0 || types.includes('locality')
}

const buildDistanceRangeValidation = (ranges, maxDistance) => {
  const errors = {
    section: [],
    min: {},
    max: {},
  }

  const addRangeError = (field, index, message) => {
    errors[field][index] = combineErrorMessages(errors[field][index] ?? [], [message])
  }

  if (!ranges.length) {
    errors.section.push('Add at least one driving distance fee range')
    return errors
  }

  let expectedMin = 0
  let canValidateEnd = true

  ranges.forEach((range, index) => {
    const min = parseDecimalInputValue(range.min_miles)
    const max = parseDecimalInputValue(range.max_miles)

    if (min === null) {
      addRangeError('min', index, 'Start miles is required')
      canValidateEnd = false
      return
    }

    if (max === null) {
      addRangeError('max', index, 'End miles is required')
      canValidateEnd = false
      return
    }

    if (Math.abs(min - expectedMin) > 0.009) {
      addRangeError(
        'min',
        index,
        `Start this range at ${formatDecimalInputValue(expectedMin)} miles so there is no gap or overlap`
      )
    }

    if (max <= min) {
      addRangeError('max', index, 'End miles must be greater than start miles')
      canValidateEnd = false
    }

    expectedMin = max
  })

  const parsedMaxDistance = parseDecimalInputValue(maxDistance)
  if (
    canValidateEnd &&
    parsedMaxDistance !== null &&
    parsedMaxDistance > 0 &&
    Math.abs(expectedMin - parsedMaxDistance) > 0.009
  ) {
    errors.section.push('Distance ranges must end at the maximum driving distance')
  }

  return errors
}

export const getServiceAreaRowKey = (type, row, index) => {
  if (row?.id !== null && row?.id !== undefined) {
    return `${type}:id:${row.id}`
  }

  if (type === 'city') {
    const placeId = row?.google_place_id || row?.place_id
    if (placeId) {
      return `${type}:place:${placeId}`
    }

    const country = row?.country || Country.UnitedStates.Value
    const state = row?.state || ''
    const city = String(row?.city || '')
      .trim()
      .toLowerCase()

    if (city || state) {
      return `${type}:value:${country}:${state}:${city}`
    }
  }

  if (type === 'postal-code' && row?.postal_code) {
    return `${type}:value:${row.postal_code}`
  }

  return `${type}:index:${index}`
}

export const getDistanceRangeRowKey = (range, index) => {
  if (range?.id !== null && range?.id !== undefined) {
    return `distance-range:id:${range.id}`
  }

  return `distance-range:index:${index}`
}

const formatImportedPostalFee = (fee) => {
  const parsedFee = Number(fee)

  return Number.isFinite(parsedFee) ? parsedFee.toFixed(2) : fee
}

const postalImportStatusBadgeVariant = {
  added: 'success',
  updated: 'info',
  unchanged: 'secondary',
  invalid: 'destructive',
}

const postalImportStatusLabel = {
  added: 'Added',
  updated: 'Updated',
  unchanged: 'Unchanged',
  invalid: 'Invalid',
}

const postalImportPreviewColumns = [
  {
    accessorKey: 'row_number',
    header: 'Row',
    meta: {
      label: 'Row',
      kind: 'number',
    },
  },
  {
    id: 'status',
    accessorFn: (row) => postalImportStatusLabel[row.status] ?? row.status,
    header: 'Status',
    meta: {
      label: 'Status',
      kind: 'badge',
    },
    cell: ({ row }) => {
      const status = row.original.status

      return (
        <Badge variant={postalImportStatusBadgeVariant[status] ?? 'secondary'}>
          {postalImportStatusLabel[status] ?? status}
        </Badge>
      )
    },
  },
  {
    accessorKey: 'postal_code',
    header: 'ZIP Code',
    meta: {
      label: 'ZIP Code',
      kind: 'primary',
    },
  },
  {
    id: 'fee',
    accessorFn: (row) => row.fee ?? '',
    header: 'Price',
    meta: {
      label: 'Price',
      kind: 'number',
    },
    cell: ({ row }) => {
      const fee = row.original.fee

      return fee === null || fee === undefined || fee === ''
        ? 'No price'
        : `$${formatImportedPostalFee(fee)}`
    },
  },
  {
    id: 'reason',
    accessorFn: (row) => row.reason ?? '',
    header: 'Note',
    meta: {
      label: 'Note',
      kind: 'text',
      preferredWidth: '18rem',
      minWidth: '12rem',
    },
    cell: ({ row }) => row.original.reason ?? '',
  },
]

function getPostalImportRowPresentation(row) {
  if (row.status === 'invalid') {
    return { tone: 'destructive' }
  }

  if (row.status === 'added') {
    return { tone: 'success' }
  }

  if (row.status === 'updated') {
    return { tone: 'info' }
  }

  return { tone: 'neutral' }
}

function getImportErrorDescription(error, fallbackMessage) {
  const fieldErrors = error?.response?.data?.errors
  const firstFieldMessage = fieldErrors
    ? Object.values(fieldErrors)
        .flat()
        .find((message) => typeof message === 'string' && message.length > 0)
    : null

  if (firstFieldMessage) {
    return firstFieldMessage
  }

  const message = getErrorMessage(error)

  if (typeof message !== 'string' || message.trim() === '') {
    return fallbackMessage
  }

  return message.length > 220 ? `${message.slice(0, 217)}...` : message
}

function ZipImportSummaryCard({ label, value }) {
  return (
    <div className="rounded-md border border-border/70 p-3">
      <Typography variant="caption" className="block">
        {label}
      </Typography>
      <span className="text-lg font-semibold">{value}</span>
    </div>
  )
}

function ImportPreviewDialog({
  errorState,
  file,
  isImporting,
  isPreviewing,
  onClearFile,
  onFileSelect,
  onImport,
  onOpenChange,
  onPreview,
  open,
  preview,
}) {
  const rows = preview?.rows ?? []
  const summary = preview?.summary ?? {}
  const canImport = (summary.added ?? 0) + (summary.updated ?? 0) > 0

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent size="3xlarge" viewport="scroll" className="max-h-[90dvh]">
        <DialogHeader>
          <DialogTitle>Import zip fees</DialogTitle>
          <DialogDescription>
            Choose a CSV or XLSX file with columns named "Zip Code" and "Price". Matching zip codes
            will be updated, new zip codes will be added, and missing zip codes will stay unchanged.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <FileDropzone
            id="delivery-postal-code-fee-import-file"
            label="Zip fee file (.xlsx or .csv)"
            accept=".xlsx,.csv"
            file={file}
            onFileSelect={onFileSelect}
            onClear={onClearFile}
            disabled={isPreviewing || isImporting}
            description='Required headers: "Zip Code" and "Price". The importer also accepts common fee headers like "Delivery Fee" or "Fee".'
          />

          {errorState ? (
            <Alert variant="destructive">
              <AlertCircle />
              <AlertTitle>{errorState.title}</AlertTitle>
              <AlertDescription>
                <p>{errorState.message}</p>
              </AlertDescription>
            </Alert>
          ) : null}

          {preview ? (
            <>
              <div className="grid grid-cols-2 gap-2 text-sm sm:grid-cols-4">
                <ZipImportSummaryCard label="Added" value={summary.added ?? 0} />
                <ZipImportSummaryCard label="Updated" value={summary.updated ?? 0} />
                <ZipImportSummaryCard label="Unchanged" value={summary.unchanged ?? 0} />
                <ZipImportSummaryCard label="Invalid" value={summary.invalid ?? 0} />
              </div>

              <IndexDataTable
                mode="client"
                pagination
                data={rows}
                columns={postalImportPreviewColumns}
                getRowId={(row) => `${row.row_number}-${row.postal_code}-${row.status}`}
                getRowPresentation={getPostalImportRowPresentation}
                defaultPageSize={25}
                maxPageSize={100}
                scrollAreaClassName="max-h-[24rem]"
                emptyState={
                  <EmptyState
                    size="compact"
                    title="No preview rows found"
                    description="The selected file did not produce any zip fee rows."
                  />
                }
              />
            </>
          ) : null}
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            type="button"
            variant={preview ? 'outline' : 'default'}
            onClick={onPreview}
            disabled={!file || isPreviewing || isImporting}
          >
            <Upload className="h-4 w-4" />
            {isPreviewing ? 'Previewing...' : 'Preview Import'}
          </Button>
          <Button
            type="button"
            onClick={onImport}
            disabled={!canImport || isPreviewing || isImporting}
          >
            <Upload className="h-4 w-4" />
            {isImporting ? 'Importing...' : 'Import zips'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default function DeliveryServiceAreas({ location = null, showDistancePricing = true }) {
  const { isReadOnly } = useIsReadOnly()
  const form = useFormContext()
  const citySearchInputRef = useRef(null)
  const [citySearch, setCitySearch] = useState('')
  const [citySuggestions, setCitySuggestions] = useState([])
  const [selectedImportFile, setSelectedImportFile] = useState(null)
  const [importPreview, setImportPreview] = useState(null)
  const [importErrorState, setImportErrorState] = useState(null)
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false)
  const { fetchSuggestions, getPlaceDetails } = usePlacesAutocomplete()
  const previewImportMutation = usePreviewLocationDeliveryPostalCodeFeeImportMutation()
  const importMutation = useImportLocationDeliveryPostalCodeFeesMutation()

  const postalFees = useStore(form.store, (state) => state.values.delivery_postal_code_fees ?? [])
  const cityFees = useStore(form.store, (state) => state.values.delivery_city_fees ?? [])
  const hasDistancePricing = useStore(
    form.store,
    (state) => state.values.is_distance_pricing_enabled
  )
  const fieldMeta = useStore(form.store, (state) => state.fieldMeta ?? {})
  const maxDistance = useStore(form.store, (state) => state.values.delivery_max_distance_miles)
  const distanceRanges = useStore(
    form.store,
    (state) => state.values.delivery_distance_fee_ranges ?? []
  )
  const hasCutoffOverrides = useStore(
    form.store,
    (state) => state.values.is_delivery_cutoff_overrides_enabled
  )
  const distanceRangeValidation = buildDistanceRangeValidation(distanceRanges, maxDistance)
  const baseServiceAreaErrors = getFieldErrorMessages(fieldMeta, postalField)
  const distanceRangeSectionErrors = combineErrorMessages(
    getFieldErrorMessages(fieldMeta, distanceRangeField),
    distanceRangeValidation.section
  )
  const maxDistanceErrors = getFieldErrorMessages(fieldMeta, maxDistanceField)
  const cutoffOverrideErrors = getFieldErrorMessages(fieldMeta, cutoffOverridesField)

  const canImport = Boolean(location?.id) && !isReadOnly

  useEffect(() => {
    let isCurrent = true

    async function loadSuggestions() {
      if (citySearch.trim().length < 3) {
        setCitySuggestions([])
        return
      }

      setCitySuggestions([])

      const suggestions = await fetchSuggestions(citySearch, {
        includedPrimaryTypes: cityAutocompletePrimaryTypes,
      })
      if (isCurrent) {
        setCitySuggestions(suggestions.slice(0, 5))
      }
    }

    void loadSuggestions()

    return () => {
      isCurrent = false
    }
  }, [citySearch, fetchSuggestions])

  const clearCitySearch = () => {
    setCitySearch('')
    setCitySuggestions([])
  }

  const focusCitySearch = () => {
    requestAnimationFrame(() => citySearchInputRef.current?.focus())
  }

  const addPostalCode = () => {
    form.setFieldValue(postalField, [
      ...postalFees,
      { id: `new-${Date.now()}`, postal_code: '', fee: 0, delivery_cutoff: null },
    ])
  }

  const removePostalCode = (index) => {
    form.setFieldValue(
      postalField,
      postalFees.filter((_, rowIndex) => rowIndex !== index)
    )
  }

  const addCity = async (suggestion) => {
    const details = await getPlaceDetails(suggestion.value)
    const city =
      details.locality ||
      details.neighborhood ||
      String(suggestion.name || '')
        .split(',')[0]
        ?.trim() ||
      ''
    const state = details.administrative_area_level_1 || ''
    const country = details.country || Country.UnitedStates.Value

    if (!city || !state) {
      toast.error('Choose a city result that includes a state.')
      focusCitySearch()
      return
    }

    const currentCityFees = form.store.state.values.delivery_city_fees ?? []
    const key = `${country}|${state}|${city.toLowerCase()}`
    const exists = currentCityFees.some(
      (fee) =>
        `${fee.country || Country.UnitedStates.Value}|${fee.state}|${String(fee.city || '').toLowerCase()}` ===
        key
    )

    if (exists) {
      toast.error('That city is already in the service area.')
      focusCitySearch()
      return
    }

    form.setFieldValue(cityField, [
      ...currentCityFees,
      {
        city,
        state,
        country,
        display_name: formatCityDisplayName(city, state, country),
        google_place_id: isCityPlaceSuggestion(suggestion)
          ? details.placeId || suggestion.id
          : null,
        fee: 0,
        delivery_cutoff: null,
      },
    ])
    setCitySearch('')
    setCitySuggestions([])
    focusCitySearch()
  }

  const removeCity = (index) => {
    form.setFieldValue(
      cityField,
      cityFees.filter((_, rowIndex) => rowIndex !== index)
    )
  }

  const setDistanceEnabled = (enabled) => {
    form.setFieldValue('is_distance_pricing_enabled', enabled)

    if (enabled && distanceRanges.length === 0) {
      form.setFieldValue(distanceRangeField, [
        { min_miles: 0, max_miles: 10, fee: 0, delivery_cutoff: null },
      ])
      form.setFieldValue('delivery_max_distance_miles', maxDistance ?? 10)
    }
  }

  const addDistanceRange = () => {
    const lastRange = distanceRanges[distanceRanges.length - 1]
    const start = Number(lastRange?.max_miles ?? 0)
    const configuredMax = Number(maxDistance)
    const end = Number.isFinite(configuredMax) && configuredMax > start ? configuredMax : start + 5
    form.setFieldValue(distanceRangeField, [
      ...distanceRanges,
      { min_miles: start, max_miles: end, fee: 0, delivery_cutoff: null },
    ])
  }

  const mergeImportedPostalFeesIntoForm = (rows) => {
    const currentPostalFees = form.getFieldValue(postalField) ?? []
    const current = new Map(currentPostalFees.map((fee) => [String(fee.postal_code), fee]))

    rows
      .filter((row) => row.status === 'added' || row.status === 'updated')
      .forEach((row) => {
        current.set(String(row.postal_code), {
          ...(current.get(String(row.postal_code)) ?? {}),
          postal_code: String(row.postal_code),
          fee: formatImportedPostalFee(row.fee),
        })
      })

    form.setFieldValue(postalField, [...current.values()])
  }

  const openImportDialog = () => {
    setSelectedImportFile(null)
    setImportPreview(null)
    setImportErrorState(null)
    setIsImportDialogOpen(true)
  }

  const handleImportFileSelect = (file) => {
    if (!file) {
      return
    }

    setSelectedImportFile(file)
    setImportPreview(null)
    setImportErrorState(null)
  }

  const handleImportFileClear = () => {
    setSelectedImportFile(null)
    setImportPreview(null)
    setImportErrorState(null)
  }

  const previewImportFile = async () => {
    if (!selectedImportFile || !location?.id) {
      const message = 'Choose a CSV or XLSX file before previewing.'

      setImportErrorState({
        title: 'Choose a file',
        message,
      })
      toast.error(message)
      return
    }

    try {
      const preview = await previewImportMutation.mutateAsync({
        locationId: location.id,
        file: selectedImportFile,
      })
      setImportPreview(preview)
      setImportErrorState(null)
      toast.success('Zip fee import preview ready.')
    } catch (error) {
      const description = getImportErrorDescription(error, 'Please review the file and try again.')

      setImportErrorState({
        title: 'Preview failed',
        message: description,
      })
      toast.error('Unable to preview zip fee import', {
        description,
      })
    }
  }

  const confirmImport = async () => {
    if (!selectedImportFile || !location?.id) return

    try {
      const result = await importMutation.mutateAsync({
        locationId: location.id,
        file: selectedImportFile,
      })
      mergeImportedPostalFeesIntoForm(result.rows ?? [])
      setIsImportDialogOpen(false)
      setImportErrorState(null)
      toast.success('Zip fees imported')
    } catch (error) {
      const description = getImportErrorDescription(error, 'Please review the file and try again.')

      setImportErrorState({
        title: 'Import failed',
        message: description,
      })
      toast.error('Unable to import zip fees', {
        description,
      })
    }
  }

  return (
    <div className="@container space-y-5">
      <section className="space-y-3 rounded-md border p-4">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="max-w-2xl">
            <Typography variant="cardTitle">Destination-based delivery cutoffs</Typography>
            <Typography variant="supporting">
              Set a different delivery cutoff for individual ZIP codes, cities, driving-distance
              ranges, or special price places. Rules left blank use the location default.
            </Typography>
            <InlineFieldErrors errors={cutoffOverrideErrors} className="mt-1" />
          </div>
          <div className="flex items-center gap-2 rounded-md border px-3 py-2">
            <Label htmlFor="delivery-cutoff-overrides-toggle">Use custom delivery cutoffs</Label>
            <Switch
              type="button"
              id="delivery-cutoff-overrides-toggle"
              checked={Boolean(hasCutoffOverrides)}
              onCheckedChange={(enabled) => form.setFieldValue(cutoffOverridesField, enabled)}
              disabled={isReadOnly}
            />
          </div>
        </div>

        {hasCutoffOverrides ? (
          <Alert variant="warning">
            <AlertCircle className="text-status-warning-foreground" />
            <AlertTitle>Same-Day Delivery Banner unavailable</AlertTitle>
            <AlertDescription>
              The storefront cannot show one countdown when the cutoff depends on the delivery
              address. Your saved banner preference will return if custom delivery cutoffs are
              disabled.
            </AlertDescription>
          </Alert>
        ) : null}
      </section>

      <section className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <Typography variant="cardTitle">Base service area</Typography>
            <Typography variant="supporting">
              Add at least one zip code or city before accepting delivery orders. If both match, the
              zip code fee is used.
            </Typography>
            <InlineFieldErrors errors={baseServiceAreaErrors} className="mt-1" />
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={!canImport}
            title={canImport ? 'Import zip fees' : 'Save the location before importing zip fees'}
            onClick={openImportDialog}
          >
            <FileUp className="h-4 w-4" />
            Import zips
          </Button>
        </div>

        <div className="grid gap-4 @5xl:grid-cols-2">
          <div className="@container/city-fees rounded-md border p-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <Typography variant="label">Cities</Typography>
              <MapPin className="h-4 w-4 text-muted-foreground" />
            </div>
            <div
              className="mb-3"
              onKeyDownCapture={(event) => {
                if (event.key === 'Enter' && citySearch.trim() === '') {
                  event.preventDefault()
                  event.currentTarget.closest('form')?.requestSubmit()
                }
              }}
            >
              <AutocompleteSearchInput
                inputRef={citySearchInputRef}
                value={citySearch}
                setValue={setCitySearch}
                suggestions={citySuggestions}
                onSelect={addCity}
                placeholder="Search for a city"
                disabled={isReadOnly}
                onClear={clearCitySearch}
                getOptionKey={(suggestion) => suggestion.id}
                getOptionLabel={(suggestion) => suggestion.name}
              />
            </div>
            {cityFees.length > 0 && (
              <div
                className={cn(
                  'mb-1 hidden gap-2 px-1 text-xs text-muted-foreground @xl/city-fees:grid',
                  hasCutoffOverrides
                    ? 'grid-cols-[minmax(10rem,1fr)_8rem_11rem_2rem]'
                    : 'grid-cols-[minmax(10rem,1fr)_8rem_2rem]'
                )}
              >
                <span>City</span>
                <span>Fallback fee</span>
                {hasCutoffOverrides ? <span>Delivery cutoff</span> : null}
                <span />
              </div>
            )}
            <div className="space-y-2">
              {cityFees.length === 0 && (
                <Typography variant="supporting">No cities yet.</Typography>
              )}
              {cityFees.map((fee, index) => {
                const cityErrorId = `delivery-city-${index}-error`
                const cityErrors = getFieldErrorMessages(
                  fieldMeta,
                  getArrayFieldPath(cityField, index, 'city')
                )

                return (
                  <div
                    key={getServiceAreaRowKey('city', fee, index)}
                    className={cn(
                      'grid grid-cols-1 items-start gap-2',
                      hasCutoffOverrides
                        ? '@xl/city-fees:grid-cols-[minmax(10rem,1fr)_8rem_11rem_2rem]'
                        : '@xl/city-fees:grid-cols-[minmax(10rem,1fr)_8rem_2rem]'
                    )}
                  >
                    <div className="min-w-0 space-y-1">
                      <Label className="@xl/city-fees:hidden">City</Label>
                      <div
                        aria-invalid={cityErrors.length > 0}
                        aria-describedby={cityErrors.length > 0 ? cityErrorId : undefined}
                        className={cn(
                          'flex h-9 min-w-0 items-center rounded-md border px-3 text-sm',
                          cityErrors.length > 0 && 'border-destructive'
                        )}
                      >
                        <div className="truncate font-medium leading-none">
                          {formatCityDisplayName(
                            fee.city,
                            fee.state,
                            fee.country || Country.UnitedStates.Value
                          )}
                        </div>
                      </div>
                      <InlineFieldErrors id={cityErrorId} errors={cityErrors} />
                    </div>
                    <div className="min-w-0 space-y-1">
                      <Label className="@xl/city-fees:hidden">Fallback fee</Label>
                      <form.AppField name={getArrayFieldPath(cityField, index, 'fee')}>
                        {(field) => <field.MoneyInput placeholder="0.00" className="h-9" />}
                      </form.AppField>
                    </div>
                    {hasCutoffOverrides ? (
                      <div className="min-w-0 space-y-1">
                        <Label className="@xl/city-fees:hidden">Delivery cutoff</Label>
                        <DeliveryCutoffOverrideField
                          ariaLabel={`${fee.city} delivery cutoff`}
                          name={getArrayFieldPath(cityField, index, 'delivery_cutoff')}
                        />
                      </div>
                    ) : null}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="justify-self-end @xl/city-fees:justify-self-auto"
                      disabled={isReadOnly}
                      onClick={() => removeCity(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="@container/postal-fees rounded-md border p-3">
            <div className="mb-2 flex items-center justify-between gap-3">
              <Typography variant="label">Zip codes</Typography>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={addPostalCode}
                disabled={isReadOnly}
              >
                <Plus className="h-4 w-4" />
                Add zip
              </Button>
            </div>
            {postalFees.length > 0 && (
              <div
                className={cn(
                  'mb-1 hidden gap-2 px-1 text-xs text-muted-foreground @xl/postal-fees:grid',
                  hasCutoffOverrides
                    ? 'grid-cols-[minmax(7rem,1fr)_2.25rem_8rem_11rem_2rem]'
                    : 'grid-cols-[minmax(7rem,1fr)_2.25rem_8rem_2rem]'
                )}
              >
                <span>ZIP code</span>
                <span />
                <span>Fallback fee</span>
                {hasCutoffOverrides ? <span>Delivery cutoff</span> : null}
                <span />
              </div>
            )}
            <div className="space-y-2">
              {postalFees.length === 0 && (
                <Typography variant="supporting">No zip codes yet.</Typography>
              )}
              {postalFees.map((fee, index) => {
                const postalCodeErrorId = `delivery-postal-code-${index}-error`
                const postalCodeFieldPath = getArrayFieldPath(postalField, index, 'postal_code')
                const postalCodeErrors = getFieldErrorMessages(fieldMeta, postalCodeFieldPath)
                const postalCode = String(fee.postal_code ?? '').trim()
                const canOpenPostalCodeMap = isValidPostalCode(postalCode)

                return (
                  <div
                    key={getServiceAreaRowKey('postal-code', fee, index)}
                    className={cn(
                      'grid grid-cols-[minmax(0,1fr)_2.25rem] items-start gap-2',
                      hasCutoffOverrides
                        ? '@xl/postal-fees:grid-cols-[minmax(7rem,1fr)_2.25rem_8rem_11rem_2rem]'
                        : '@xl/postal-fees:grid-cols-[minmax(7rem,1fr)_2.25rem_8rem_2rem]'
                    )}
                  >
                    <form.AppField name={postalCodeFieldPath}>
                      {(field) => (
                        <div className="min-w-0 space-y-1">
                          <Label className="@xl/postal-fees:hidden">ZIP code</Label>
                          <Input
                            value={field.state.value ?? ''}
                            maxLength={5}
                            placeholder="12345"
                            disabled={isReadOnly}
                            aria-invalid={postalCodeErrors.length > 0}
                            aria-describedby={
                              postalCodeErrors.length > 0 ? postalCodeErrorId : undefined
                            }
                            onChange={(event) =>
                              field.handleChange(event.target.value.replace(/\D/g, '').slice(0, 5))
                            }
                            onBlur={field.handleBlur}
                          />
                          <InlineFieldErrors id={postalCodeErrorId} errors={postalCodeErrors} />
                        </div>
                      )}
                    </form.AppField>
                    {canOpenPostalCodeMap ? (
                      <Button
                        asChild
                        type="button"
                        variant="ghostMuted"
                        size="icon-sm"
                        className="self-end @xl/postal-fees:self-start"
                        title={`Open ${postalCode} in Google Maps`}
                      >
                        <a
                          href={`https://www.google.com/maps/place/${encodeURIComponent(postalCode)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          aria-label={`Open ${postalCode} in Google Maps`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      </Button>
                    ) : (
                      <Button
                        type="button"
                        variant="ghostMuted"
                        size="icon-sm"
                        className="self-end @xl/postal-fees:self-start"
                        disabled
                        title="Enter a 5-digit ZIP code to view it in Google Maps"
                        aria-label="Open ZIP code in Google Maps"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    )}
                    <div className="col-span-2 min-w-0 space-y-1 @xl/postal-fees:col-span-1">
                      <Label className="@xl/postal-fees:hidden">Fallback fee</Label>
                      <form.AppField name={getArrayFieldPath(postalField, index, 'fee')}>
                        {(field) => <field.MoneyInput placeholder="0.00" className="h-9" />}
                      </form.AppField>
                    </div>
                    {hasCutoffOverrides ? (
                      <div className="col-span-2 min-w-0 space-y-1 @xl/postal-fees:col-span-1">
                        <Label className="@xl/postal-fees:hidden">Delivery cutoff</Label>
                        <DeliveryCutoffOverrideField
                          ariaLabel={`${postalCode || 'ZIP code'} delivery cutoff`}
                          name={getArrayFieldPath(postalField, index, 'delivery_cutoff')}
                        />
                      </div>
                    ) : null}
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="col-start-2 justify-self-end @xl/postal-fees:col-auto @xl/postal-fees:justify-self-auto"
                      disabled={isReadOnly}
                      onClick={() => removePostalCode(index)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </section>

      {showDistancePricing && (
        <>
          <Alert variant="warning">
            <AlertCircle className="text-status-warning-foreground" />
            <AlertTitle>Keep base rules accurate</AlertTitle>
            <AlertDescription>
              Advanced delivery rules below require a verified address. If checkout cannot verify
              the address or calculate a route, it falls back to the zip code and city fees above.
            </AlertDescription>
          </Alert>

          <section className="rounded-md border p-4">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="flex items-start gap-2">
                <Route className="mt-0.5 h-4 w-4 text-muted-foreground" />
                <div>
                  <Typography variant="cardTitle">Driving distance pricing</Typography>
                  <Typography variant="supporting">
                    When checkout can verify and route the address, these ranges replace zip and
                    city fees.
                  </Typography>
                </div>
              </div>
              <div className="flex items-center gap-2 rounded-md border px-3 py-2">
                <Label htmlFor="delivery-distance-pricing-toggle">Use distance fees</Label>
                <Switch
                  id="delivery-distance-pricing-toggle"
                  checked={hasDistancePricing}
                  onCheckedChange={setDistanceEnabled}
                  disabled={isReadOnly}
                />
              </div>
            </div>

            {hasDistancePricing && (
              <div className="mt-4 grid gap-4 @5xl:grid-cols-[18rem_minmax(0,1fr)]">
                <div className="space-y-1.5">
                  <Label htmlFor="delivery-max-distance-miles">Maximum driving miles</Label>
                  <DecimalInput
                    id="delivery-max-distance-miles"
                    value={maxDistance}
                    decimalPlaces={1}
                    disabled={isReadOnly}
                    aria-invalid={maxDistanceErrors.length > 0}
                    aria-describedby={
                      maxDistanceErrors.length > 0 ? 'delivery-max-distance-error' : undefined
                    }
                    className={cn(
                      maxDistanceErrors.length > 0 &&
                        'border-destructive focus-visible:ring-destructive'
                    )}
                    onValueChange={(value) => form.setFieldValue(maxDistanceField, value)}
                    onBlurValueChange={(value) => form.setFieldValue(maxDistanceField, value)}
                  />
                  <InlineFieldErrors id="delivery-max-distance-error" errors={maxDistanceErrors} />
                  <Typography variant="caption" className="block">
                    Checkout uses real route driving distance from this location. Special price
                    places ignore this limit.
                  </Typography>
                </div>

                <div className="@container/distance-ranges space-y-2">
                  <InlineFieldErrors errors={distanceRangeSectionErrors} className="px-1" />
                  <div
                    className={cn(
                      'hidden gap-2 px-1 text-xs text-muted-foreground @xl/distance-ranges:grid',
                      hasCutoffOverrides
                        ? 'grid-cols-[minmax(4.5rem,.7fr)_minmax(4.5rem,.7fr)_minmax(7rem,1fr)_11rem_2rem]'
                        : 'grid-cols-[minmax(4.5rem,.7fr)_minmax(4.5rem,.7fr)_minmax(7rem,1fr)_2rem]'
                    )}
                  >
                    <span>From miles</span>
                    <span>To miles</span>
                    <span>Delivery fee</span>
                    {hasCutoffOverrides ? <span>Delivery cutoff</span> : null}
                    <span />
                  </div>
                  {distanceRanges.map((range, index) => {
                    const minMilesErrorId = `delivery-distance-range-${index}-min-error`
                    const maxMilesErrorId = `delivery-distance-range-${index}-max-error`
                    const minMilesErrors = getFieldErrorMessages(
                      fieldMeta,
                      getArrayFieldPath(distanceRangeField, index, 'min_miles')
                    )
                    const maxMilesErrors = combineErrorMessages(
                      getFieldErrorMessages(
                        fieldMeta,
                        getArrayFieldPath(distanceRangeField, index, 'max_miles')
                      ),
                      distanceRangeValidation.max[index] ?? []
                    )
                    const combinedMinMilesErrors = combineErrorMessages(
                      minMilesErrors,
                      distanceRangeValidation.min[index] ?? []
                    )

                    return (
                      <div
                        key={getDistanceRangeRowKey(range, index)}
                        className={cn(
                          'grid grid-cols-2 items-start gap-2',
                          hasCutoffOverrides
                            ? '@xl/distance-ranges:grid-cols-[minmax(4.5rem,.7fr)_minmax(4.5rem,.7fr)_minmax(7rem,1fr)_11rem_2rem]'
                            : '@xl/distance-ranges:grid-cols-[minmax(4.5rem,.7fr)_minmax(4.5rem,.7fr)_minmax(7rem,1fr)_2rem]'
                        )}
                      >
                        <div className="min-w-0 space-y-1">
                          <Label className="@xl/distance-ranges:hidden">From miles</Label>
                          <DecimalInput
                            decimalPlaces={1}
                            value={range.min_miles}
                            disabled={isReadOnly || index === 0}
                            aria-label={`Range ${index + 1} start miles`}
                            aria-invalid={combinedMinMilesErrors.length > 0}
                            aria-describedby={
                              combinedMinMilesErrors.length > 0 ? minMilesErrorId : undefined
                            }
                            className={cn(
                              combinedMinMilesErrors.length > 0 &&
                                'border-destructive focus-visible:ring-destructive'
                            )}
                            onValueChange={(value) =>
                              form.setFieldValue(
                                distanceRangeField,
                                updateRow(distanceRanges, index, {
                                  min_miles: value,
                                })
                              )
                            }
                          />
                          <InlineFieldErrors id={minMilesErrorId} errors={combinedMinMilesErrors} />
                        </div>
                        <div className="min-w-0 space-y-1">
                          <Label className="@xl/distance-ranges:hidden">To miles</Label>
                          <DecimalInput
                            decimalPlaces={1}
                            value={range.max_miles}
                            disabled={isReadOnly}
                            aria-label={`Range ${index + 1} end miles`}
                            aria-invalid={maxMilesErrors.length > 0}
                            aria-describedby={
                              maxMilesErrors.length > 0 ? maxMilesErrorId : undefined
                            }
                            className={cn(
                              maxMilesErrors.length > 0 &&
                                'border-destructive focus-visible:ring-destructive'
                            )}
                            onValueChange={(value) =>
                              form.setFieldValue(
                                distanceRangeField,
                                updateRow(distanceRanges, index, {
                                  max_miles: value,
                                })
                              )
                            }
                          />
                          <InlineFieldErrors id={maxMilesErrorId} errors={maxMilesErrors} />
                        </div>
                        <div className="col-span-2 min-w-0 space-y-1 @xl/distance-ranges:col-span-1">
                          <Label className="@xl/distance-ranges:hidden">Delivery fee</Label>
                          <form.AppField name={getArrayFieldPath(distanceRangeField, index, 'fee')}>
                            {(field) => <field.MoneyInput placeholder="0.00" className="h-9" />}
                          </form.AppField>
                        </div>
                        {hasCutoffOverrides ? (
                          <div className="col-span-2 min-w-0 space-y-1 @xl/distance-ranges:col-span-1">
                            <Label className="@xl/distance-ranges:hidden">Delivery cutoff</Label>
                            <DeliveryCutoffOverrideField
                              ariaLabel={`Range ${index + 1} delivery cutoff`}
                              name={getArrayFieldPath(distanceRangeField, index, 'delivery_cutoff')}
                            />
                          </div>
                        ) : null}
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="col-start-2 justify-self-end @xl/distance-ranges:col-auto @xl/distance-ranges:justify-self-auto"
                          disabled={isReadOnly || distanceRanges.length === 1}
                          onClick={() =>
                            form.setFieldValue(
                              distanceRangeField,
                              distanceRanges.filter((_, rowIndex) => rowIndex !== index)
                            )
                          }
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )
                  })}
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={addDistanceRange}
                    disabled={isReadOnly}
                  >
                    <Plus className="h-4 w-4" />
                    Add range
                  </Button>
                </div>
              </div>
            )}
          </section>
        </>
      )}

      <ImportPreviewDialog
        open={isImportDialogOpen}
        onOpenChange={setIsImportDialogOpen}
        file={selectedImportFile}
        preview={importPreview}
        errorState={importErrorState}
        onFileSelect={handleImportFileSelect}
        onClearFile={handleImportFileClear}
        onPreview={previewImportFile}
        onImport={confirmImport}
        isPreviewing={previewImportMutation.isPending}
        isImporting={importMutation.isPending}
      />
    </div>
  )
}
